/**
 * Hybrid AHU tunnel addressing.
 *
 * Non-hybrid AHUs address a tunnel by `row:level` (position is always 1 and
 * stays hidden). Hybrid AHUs add the 3rd field (`position`, the back-to-back
 * axis) so two physically distinct tunnels sharing a (row, level) cell get
 * distinct labels.
 *
 * This is the single canonical formatter — every place that renders a tunnel
 * address (AHU tab tunnel list, tunnel-index pill strip, sizer popup, lifting
 * lug pane) should import this instead of re-deriving the string, mirroring
 * the rule the legacy Backbone board applied ad hoc in four different files
 * (fn.tools, sizer_popup.js, tab.ahu.js, tab.lifting.lug.js).
 */

export interface IAddressable {
  row?: number | null;
  level?: number | null;
  position?: number | null;
}

/** `"row:level"` for non-hybrid, `"row:level:position"` for hybrid. */
export function formatTunnelAddress(
  tunnel: IAddressable,
  isHybrid: boolean | undefined
): string {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  const position = tunnel.position ?? 1;
  return isHybrid ? `${row}:${level}:${position}` : `${row}:${level}`;
}

/** `"row:level"` only — used to key/group back-to-back peers together. */
export function formatRowLevel(tunnel: IAddressable): string {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  return `${row}:${level}`;
}

/**
 * Is this unit's tunnel set hybrid-SHAPED — does any (row, level) cell hold
 * more than one tunnel, or does any tunnel sit at a position above 1?
 *
 * Both halves on purpose, to match the server: `get_tunnel_configuration()`
 * reports Hybrid on `max_position > 1 or is_hybrid`, which is the same thing
 * as a shared cell on normally renumbered data (`_normalize_tunnel_addressing`
 * renumbers positions from 1 per cell on every delete) but not on API-created
 * rows — two tunnels can share a cell AND a position, and a lone tunnel can be
 * created at position 2.
 *
 * A standard AHU satisfies neither: one tunnel per cell, all at position 1, is
 * what standard means (0 of the box's non-hybrid units break either half).
 *
 * Worth asking separately from `AHUUnit.is_hybrid`, which is a flag the
 * board's own hybrid flow writes: a unit built through the REST API has the
 * shape without the flag, and the tunnel rows answer before the (slow) unit
 * GET has landed. `isHybridAhu` below is the question callers actually want.
 */
export function hasHybridTunnelShape(tunnels: IAddressable[]): boolean {
  const seen = new Set<string>();
  for (const tunnel of tunnels) {
    if ((tunnel.position ?? 1) > 1) return true;
    const cell = formatRowLevel(tunnel);
    if (seen.has(cell)) return true;
    seen.add(cell);
  }
  return false;
}

/**
 * Is this a hybrid AHU — by the flag the board writes, or by the shape of its
 * tunnels? THE definition for anything that has to decide before the unit GET
 * has landed, so that the flag and the shape cannot drift apart between
 * callers. (The rest of the board reads `ahu.is_hybrid` alone, which is a
 * product decision about badges and edit locks, not a timing-sensitive one —
 * see the hybrid-tunnel notes in the inventory.)
 */
export function isHybridAhu(
  unit: { is_hybrid?: boolean | null } | undefined | null,
  tunnels: IAddressable[] | undefined | null
): boolean {
  return !!unit?.is_hybrid || hasHybridTunnelShape(tunnels ?? []);
}

/** Stable sort key for row:level:position ordering (ties broken left to right). */
export function tunnelAddressRank(tunnel: IAddressable): [number, number, number] {
  return [tunnel.row ?? Infinity, tunnel.level ?? Infinity, tunnel.position ?? Infinity];
}

/**
 * `Array.sort` comparator for row:level:position order — the order the Data
 * Bar tunnel list and the tunnel-index pill strip both read in. Compared field
 * by field (never `Infinity - Infinity`, which is NaN and would make the sort
 * incoherent on a tunnel with no addressing).
 */
export function compareTunnelAddress(a: IAddressable, b: IAddressable): number {
  const ra = tunnelAddressRank(a);
  const rb = tunnelAddressRank(b);
  for (let i = 0; i < 3; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
  return 0;
}

/**
 * getVisibleTunnelIndexes (fn_tools.js) — pagination window {start, end} of
 * at most `maxVisible` items, re-centered on `activeIndex`. Shared by every
 * tunnel-index pill strip (the canvas tab strip, the Lifting Lugs panel) so
 * they all window the same way.
 */
export function getVisibleTunnelIndexes(
  activeIndex: number,
  totalItems: number,
  maxVisible = 5
): { start: number; end: number } {
  if (totalItems <= maxVisible) {
    return { start: 0, end: totalItems };
  }
  const half = Math.floor(maxVisible / 2);
  let start = activeIndex - half;
  let end = start + maxVisible;
  if (start < 0) {
    start = 0;
    end = maxVisible;
  }
  if (end > totalItems) {
    end = totalItems;
    start = end - maxVisible;
  }
  return { start, end };
}
